import { Switch, Table, Text } from "@ttc3k/trekker";
import { AnimatePresence, motion } from "framer-motion";
import type { CalendarProduct } from "../useCalendarView";
import { useEffect, useState } from "react";
import { useController, useFormContext, useWatch } from "react-hook-form";
import { ButtonCard, Collapsible } from "shared/components";
import { Box, Flex, styled } from "styled-system/jsx";
import { getMultilingualString } from "shared/utils";
import { useTranslation } from "react-i18next";
import { CreateEditClosureFormValues } from "./actions";

type SelectClosureProductsProps = {
  isEditingClosure?: boolean;
  allProducts: CalendarProduct[];
};

// motion() is deprecated, so we need to use motion.create() instead
const MotionTableRow = motion.create(Table.Row);
const MotionTableRoot = motion.create(Table.Root);
const ButtonCardWrapper = styled(Flex, {
  base: {
    transition: "width 0.2s linear",
    "&[data-products-open]": {
      width: "calc(100% - 10px)",
    },
    "&[data-products-closed]": {
      width: "100%",
    },
  },
});

const rowVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const containerVariants = {
  visible: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

export const SelectClosureProducts = ({
  isEditingClosure,
  allProducts,
}: SelectClosureProductsProps) => {
  const [isScrollbarOpen, setIsScrollbarOpen] = useState(false);
  const [closureType, setClosureType] = useState<"full" | "partial">("full");
  const { control, setValue } = useFormContext<CreateEditClosureFormValues>();

  const handleSetFullClosure = () => {
    setClosureType("full");
    setValue("productIds", allProducts?.map(p => p?._id) ?? [], {
      shouldDirty: true,
    });
  };
  const handleSetPartialClosure = () => {
    setClosureType("partial");
    setValue("productIds", [], {
      shouldDirty: true,
    });
  };

  const selectedProductIds = useWatch({
    control,
    name: "productIds",
  });
  const {
    fieldState: { error },
  } = useController({
    name: "productIds",
    control,
  });

  useEffect(() => {
    // When creating a new closure, set the product ids to all products
    // so by default, the closure is applying to the entire storefront
    if (!isEditingClosure) {
      setValue("productIds", allProducts?.map(p => p?._id) ?? []);
    }
  }, [allProducts, isEditingClosure, setValue]);

  useEffect(() => {
    // When editing a closure, set the closure type to partial if the
    // selected product ids are not the same as the total number of products
    if (isEditingClosure && selectedProductIds.length > 0) {
      const isPartialSelection = selectedProductIds.length < allProducts.length;
      if (isPartialSelection) {
        setClosureType("partial");
      } else if (selectedProductIds.length === allProducts.length) {
        setClosureType("full");
      }
    }
  }, [allProducts, selectedProductIds, isEditingClosure]);

  useEffect(() => {
    const wrapper = document.getElementById(
      "whereabouts-modal-content-wrapper",
    );

    if (wrapper) {
      const checkScrollbar = () => {
        setIsScrollbarOpen(wrapper.scrollHeight > wrapper.clientHeight);
      };
      checkScrollbar();

      const resizeObserver = new ResizeObserver(checkScrollbar);
      resizeObserver.observe(wrapper);

      const mutationObserver = new MutationObserver(checkScrollbar);
      mutationObserver.observe(wrapper, { childList: true, subtree: true });

      wrapper.addEventListener("scroll", checkScrollbar);

      return () => {
        resizeObserver.disconnect();
        mutationObserver.disconnect();
        wrapper.removeEventListener("scroll", checkScrollbar);
      };
    }
  }, []);

  const isProductsOpen = closureType === "partial" && !!allProducts;

  const conditionalProps = {
    ...(isScrollbarOpen
      ? { "data-products-open": true }
      : { "data-products-closed": true }),
  };

  const { t } = useTranslation("storefront", {
    keyPrefix: "CALENDAR.CLOSURES.LABELS.PRODUCTS",
  });

  if (allProducts.length <= 1) return null;
  return (
    <>
      <Box>
        <Text color={"text.dark"}>{t("TITLE")}</Text>
        <AnimatePresence>
          <ButtonCardWrapper
            flex={1}
            gap={"150"}
            mt={"150"}
            {...conditionalProps}
          >
            <ButtonCard
              title={t("ENTIRE_STOREFRONT.TITLE")}
              description={t("ENTIRE_STOREFRONT.DESCRIPTION")}
              onClick={handleSetFullClosure}
              isSelected={closureType === "full"}
            />
            <ButtonCard
              title={t("SPECIFIC_PRODUCTS.TITLE")}
              description={t("SPECIFIC_PRODUCTS.DESCRIPTION")}
              onClick={handleSetPartialClosure}
              isSelected={closureType === "partial"}
            />
          </ButtonCardWrapper>
        </AnimatePresence>
        {error && (
          <Text visual={"smallMedium"} color={"text.error.dark"} mt={"100"}>
            {error.message}
          </Text>
        )}
      </Box>
      <Collapsible open={isProductsOpen}>
        {isProductsOpen && (
          <MotionTableRoot
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            <Table.Body>
              {allProducts.map((product, index) => {
                if (!product) return null;
                const isProductSelected = selectedProductIds.includes(
                  product._id,
                );
                const handleCheckedChange = (checked: boolean) => {
                  if (checked) {
                    const newProductIds = [...selectedProductIds, product._id];
                    setValue("productIds", newProductIds, {
                      shouldDirty: true,
                    });
                  } else {
                    const newProductIds = selectedProductIds.filter(
                      v => v !== product._id,
                    );
                    setValue("productIds", newProductIds, {
                      shouldDirty: true,
                    });
                  }
                };
                return (
                  <MotionTableRow
                    key={product._id}
                    width={"full"}
                    variants={rowVariants}
                    transition={{ delay: index * 0.1, duration: 0.3 }}
                  >
                    <Table.Header>
                      {getMultilingualString(product.name)}
                    </Table.Header>
                    <Table.Cell>
                      <Switch
                        checked={isProductSelected}
                        onCheckedChange={e => handleCheckedChange(e.checked)}
                        justifyContent={"flex-end"}
                      />
                    </Table.Cell>
                  </MotionTableRow>
                );
              })}
            </Table.Body>
          </MotionTableRoot>
        )}
      </Collapsible>
    </>
  );
};
